import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * HTTP Interceptor that fixes the "one day earlier" date issue
 * 
 * Problem: Material DatePicker creates Date objects at midnight LOCAL time.
 * When Angular's HttpClient serializes these via JSON.stringify(), Date objects
 * are converted to ISO strings using UTC, which can shift the date by one day
 * if the user's timezone is ahead of UTC.
 * 
 * Solution: Intercept requests BEFORE JSON serialization and convert Date objects
 * to strings using local timezone methods (getFullYear, getMonth, getDate).
 * 
 * This ensures the selected date is preserved regardless of timezone.
 */
@Injectable()
export class DateSerializerInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only process requests with a body
    if (!req.body) {
      return next.handle(req);
    }

    // Serialize Date objects in the request body using local timezone
    const serializedBody = this.serializeDates(req.body);
    const cloned = req.clone({ body: serializedBody });
    
    return next.handle(cloned);
  }

  /**
   * Recursively serialize Date objects in any data structure
   * Converts Date objects to strings using local timezone components
   */
  private serializeDates(value: any): any {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return value;
    }

    // Handle Date objects - convert using local timezone
    if (value instanceof Date) {
      return this.formatDateLocal(value);
    }

    // Handle arrays - recursively process each element
    if (Array.isArray(value)) {
      return value.map(item => this.serializeDates(item));
    }

    // Handle plain objects - recursively process each property
    if (typeof value === 'object' && value.constructor === Object) {
      const result: any = {};
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          result[key] = this.serializeDates(value[key]);
        }
      }
      return result;
    }

    // Return primitive values as-is
    return value;
  }

  /**
   * Format a Date object using local timezone components
   * This prevents the date from shifting when converted to UTC
   * 
   * @param date - The Date object to format
   * @returns Formatted date string (YYYY-MM-DD for date-only, YYYY-MM-DDTHH:MM:SS for datetime)
   */
  private formatDateLocal(date: Date): string {
    // Use local timezone methods to preserve the selected date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const milliseconds = date.getMilliseconds();

    // Check if this is a date-only value (all time components are zero)
    const isDateOnly = hours === 0 && minutes === 0 && seconds === 0 && milliseconds === 0;

    if (isDateOnly) {
      // Return date-only format: YYYY-MM-DD
      return `${year}-${month}-${day}`;
    }

    // Return datetime format: YYYY-MM-DDTHH:MM:SS (or with milliseconds)
    const H = String(hours).padStart(2, '0');
    const M = String(minutes).padStart(2, '0');
    const S = String(seconds).padStart(2, '0');

    if (milliseconds > 0) {
      const ms = String(milliseconds).padStart(3, '0');
      return `${year}-${month}-${day}T${H}:${M}:${S}.${ms}`;
    }

    return `${year}-${month}-${day}T${H}:${M}:${S}`;
  }
}

